import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientService } from '../services/api';
import { FaSearch, FaBuilding, FaEye, FaPhone, FaEnvelope, FaPlus } from 'react-icons/fa';
import styled from 'styled-components';

const ClientListContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 25px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
`;

const Title = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
  font-weight: 600;
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 15px 12px 45px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const ClientGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  padding: 25px;
`;

const ClientCard = styled.div`
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  }
`;

const ClientHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const ClientName = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
  font-weight: 600;
`;

const ClientStatus = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$active ? '#155724' : '#721c24'};
`;

const ClientInfo = styled.div`
  margin-bottom: 15px;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #6c757d;
  
  svg {
    margin-right: 8px;
    width: 14px;
    color: #999;
  }
`;

const ClientStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
`;

const StatItem = styled.div`
  text-align: center;
  
  .value {
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
  }
  
  .label {
    font-size: 12px;
    color: #6c757d;
  }
`;

const ViewButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 15px;
  border-radius: 8px;
  margin: 20px;
  border: 1px solid #fcc;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
  
  svg {
    font-size: 48px;
    margin-bottom: 15px;
    color: #dee2e6;
  }
`;

const ClientList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoading(true);
        const data = await clientService.getClients();
        setClients(data);
      } catch (err) {
        setError('Erro ao carregar lista de clientes');
        console.error('Erro ao carregar clientes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, []);

  const filteredClients = clients.filter(client =>
    client.nome_fantasia?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.razao_social?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClientClick = (clientId) => {
    navigate(`/clientes/${clientId}`);
  };

  const handleAddNewClient = () => {
    navigate('/clientes/novo');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  return (
    <ClientListContainer>
      <Header>
        <Title>Clientes ({filteredClients.length})</Title>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <SearchContainer>
            <SearchIcon>
              <FaSearch />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          <AddButton onClick={handleAddNewClient}>
            <FaPlus />
            Nova Empresa
          </AddButton>
        </div>
      </Header>

      {clients.length === 0 && !loading ? (
        <EmptyState>
          <FaBuilding />
          <p>Nenhum cliente cadastrado</p>
        </EmptyState>
      ) : filteredClients.length === 0 && searchTerm ? (
        <EmptyState>
          <FaSearch />
          <p>Nenhum cliente encontrado para "{searchTerm}"</p>
        </EmptyState>
      ) : (
        <ClientGrid>
          {filteredClients.map((client) => (
            <ClientCard key={client.id} onClick={() => handleClientClick(client.id)}>
              <ClientHeader>
                <ClientName>{client.nome_fantasia}</ClientName>
                                 <ClientStatus $active={client.status === 'active'}>
                   {client.status === 'active' ? 'Ativo' : 'Inativo'}
                 </ClientStatus>
              </ClientHeader>
              
              <ClientInfo>
                <InfoRow>
                  <FaEnvelope />
                  {client.email}
                </InfoRow>
                <InfoRow>
                  <FaPhone />
                  {client.telefone || 'N/A'}
                </InfoRow>
              </ClientInfo>
              
                             <ClientStats>
                 <StatItem>
                   <div className="value">{client.users_count || 0}</div>
                   <div className="label">Usuários</div>
                 </StatItem>
                
                <ViewButton>
                  <FaEye />
                  Ver Detalhes
                </ViewButton>
              </ClientStats>
            </ClientCard>
          ))}
        </ClientGrid>
      )}
    </ClientListContainer>
  );
};

export default ClientList; 